(ns app.domain.contractor-registration.model
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.i18n.model :as i18n-model]
            [app.query.filter-util :as filter-util]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [schema.core :as s]))

(def ContractorRegistrationId s/Uuid)

(def ^{:arglists '([contractor-registration])} contractor-registration-trades
  (child/child-entities-of-type-fn :entity.type/registered-trade))

(defn role->registration-role
  "Which contractor registration role should be given to which registration application party role?"
  [role]
  (case role
    :role/applicant :role.contractor-registration/writer
    :role.contractor-registration/reader))

(def trade-attr->field-id
  "Mapping between trade attributes and the corresponding form field ids"
  {:registered-trade/master.first-name     :cr-master-first-name
   :registered-trade/master.last-name      :cr-master-last-name
   :registered-trade/master.licence-number :cr-master-licence-number})

(def trade-field-ids (set (vals trade-attr->field-id)))

(def dashboard-filter-condition-attributes
  [:domain/state
   :group/id
   :work-type-names
   :expiration-date
   :action-items
   :expiration-date-range
   :issue-date-range])

(def date-range-filter-condition-attributes
  #{:expiration-date-range
    :issue-date-range})

(def additional-search-filter-condition-attributes
  (into #{:action-items} date-range-filter-condition-attributes))

(def action-item-values
  #{:has-associated-bond?
    :has-open-bills?
    :has-in-progress-application?})

(defn additional-search-filters-enabled? [feature-flags]
  (contains? feature-flags :feature-flag.id/contractor-registration-search-filters))

(defn enabled-filter-conditions [feature-flags conditions]
  (cond->> conditions
    (not (additional-search-filters-enabled? feature-flags))
    (remove (comp additional-search-filter-condition-attributes :filter.condition/attribute))))

(defn valid-search-condition?
  [{:filter.condition/keys [attribute values]}]
  (cond
    (date-range-filter-condition-attributes attribute)
    (filter-util/valid-date-range-values? values)

    (= attribute :action-items)
    (and (set? values) (every? action-item-values values))

    :else
    true))

(def search-sort-attributes
  [:modified
   :contractor-registration/valid-from
   :contractor-registration/valid-to])

(s/defschema SearchSorting
  (s/pair (apply s/enum search-sort-attributes) "Sort attribute"
          (s/enum :asc :desc) "Sort direction"))

(def registrant-name (i18n-model/translation-fallback-fn :registrant-names))

(defn format-name
  ([registrant] (format-name registrant i18n-model/default-lang))
  ([registrant lang]
   (registrant-name registrant lang)))

(def revocation-letter-template-variables
  (concat ["cr-trades"]
          gl-model/revocation-letter-common-template-variables
          gl-model/holder-revocation-letter-template-variables))

(defn registered-trades->work-type-ids [trades]
  (->> trades
       (keep #(some-> % :registered-trade/work-type :db/id))
       set))

(defn- all-registrations [{:party/keys [user business] :as party}]
  (if-let [registrant (or user business)]
    (->> registrant
         :contractor-registration/_registrant)
    (or (when-let [party-cr (:party/contractor-registration party)]
          [party-cr])
        (->> party
             :contractor-registration/_party-registrant))))

(defn party-entity->registrations
  "Returns contractor-registrations for party's user/business, or directly for the party if it is a manually added one."
  ([party]
   (let [;; only find sibling-groups when needed
         *sibling-authority-groups (-> party
                                       :entity/parent ;; phase
                                       :entity/parent ;; workspace
                                       :domain/authority-group
                                       group-model/sibling-authority-groups
                                       delay)
         filter-auth-group         #(@*sibling-authority-groups (:domain/authority-group %))]
     (party-entity->registrations party filter-auth-group)))
  ([party filter-fn]
   (->> party
        all-registrations
        (filter filter-fn))))

(defn application-form-schema-id [group-id]
  (str group-id "/application-for-contractor-registration"))

(defn renewal-form-schema-id [group-id]
  (str group-id "/application-for-contractor-registration-renewal"))

(defmethod group-model/get-authority-group :entity.type/contractor-registration
  [{:domain/keys [authority-group]}]
  authority-group)

;; FIXME: Actual check should be via a process flow, not English name.
(defn renewal-group-config? [group-config]
  (some-> (config-model/localized-name group-config i18n-model/default-lang)
          str/lower-case
          (str/includes? "renew")))

(defn renewal-workspace? [workspace]
  (-> workspace
      :workspace/category-group-config
      renewal-group-config?))

(defn workspace-registration [workspace]
  (some->> workspace
           :contractor-registration/_workspaces
           first))

(defn source-workspace [{verdict :contractor-registration/verdict}]
  {:pre [verdict]}
  (-> verdict :entity/parent :entity/parent))

(defn new-registration-group-config?
  [group-config]
  (boolean (some->> (config-model/localized-name group-config i18n-model/default-lang)
                    ;; The word boundary ensures that f.ex. "renew registration" is not matched."
                    (re-find #"(?i)\bnew registration\b"))))
